import { useEffect, useState, useContext, useCallback } from 'react';
import { API } from '../../../API';
import { ShareAPI } from '../../../API';
import { narrativeActorsUrl, toRollupTopActorsParams } from '../../../utils/narrativeActors';
import { useTranslation } from 'react-i18next';
import { BreakdownsContext } from './contexts/BreakdownsContext';
import {
  platformIconsMapping,
  platformMapping,
} from '../../../utils/platforms';
import { BreakdownsContainer } from '../../../components/BreakdownsContainer/BreakdownsContainer';
import { LoaderSmall } from '../../../components/LoaderSmall/LoaderSmall';
import { decodeSourceName } from '../../../utils/decodeURI';
import { Tooltip } from 'react-tooltip';
import { cutString } from '../../../utils/cutString';
import { ReactComponent as AnchorIcon } from '../../SourcePage/assets/anchor.svg';
import { Paginator } from '../../../components/Paginator/Paginator';
import styles from '../NarrativePage.module.scss';
import {
  formatNumberSignificant,
  formatWithCustomGigaBillion,
} from '../../../utils/fomatNumbers';
import { showRequestError } from '../../../utils/requestError';
import { BreakdownsFiltersContent } from './Filters/BreakdownsFilters';
import {
  BreakdownsFilterProvider,
  useBreakdownsFilterContext,
} from './Filters/BreakdownsFilterContext';
import { DropdownMenu } from '../../../components/DropdownMenu/DropdownMenu';
import { Radiobutton } from '../../../components/DropdownMenu/Radiobutton';
import { ReactComponent as ChevronIcon } from '../../../components/Filters/assets/chevron.svg';
import { ReactComponent as SortIcon } from '../../../assets/sort.svg';

const reversePlatformMapping = Object.fromEntries(
  Object.entries(platformMapping).map(([name, id]) => [id, name]),
);

const buildFilterParams = (filters) => {
  const params = new URLSearchParams();

  let excludedFilters = [];

  if (filters.platforms.value.length > 0) {
    const sourceTypes = filters.platforms.value
      .map((id) => reversePlatformMapping[id])
      .filter(Boolean);
    sourceTypes.forEach((type) => params.append('source_types', type));
  }

  if (filters.countries.value.length > 0) {
    filters.countries.value.forEach((id) => params.append('country_ids', id));
    if (filters.countries.exclude) {
      excludedFilters.push('country_ids');
    }
  }

  if (
    filters.affiliations.value?.length > 0 &&
    filters.affiliations.value[0] === 'all'
  ) {
    params.append('state_affiliated', true);
    if (filters.affiliations.exclude) {
      excludedFilters.push('affiliation_country_ids');
    }
  } else if (filters.affiliations.value?.length > 0) {
    filters.affiliations.value.forEach((country) => {
      params.append('affiliation_country_ids', country);
    });

    if (filters.affiliations.exclude) {
      excludedFilters.push('affiliation_country_ids');
    }
  }

  if (filters.bots.value === true) {
    params.set('is_bot', 'true');
  }

  if (filters.bots.exclude === true) {
    excludedFilters.push('source_bots');
  }

  if (filters.discreditations.value.length > 0) {
    filters.discreditations.value.forEach((type) =>
      params.append('entity_types', type),
    );
    if (filters.discreditations.exclude) {
      excludedFilters.push('discreditated');
    }
  }

  if (filters.audience) {
    const gte = Number(filters.audience.value[0]);
    const lte = Number(filters.audience.value[1]);
    if (gte > 0) {
      params.set('audience_gte', gte);
    }
    if (lte > 0 && lte < 1000000000) {
      params.set('audience_lte', lte);
    }
    if (filters.audience.exclude) {
      excludedFilters.push('audience_lte');
    }
  }

  if (filters.geoGroup?.value?.length > 0) {
    filters.geoGroup.value.forEach((id) => params.append('country_group_ids', id));
    if (filters.geoGroup.exclude) {
      excludedFilters.push('country_group_ids');
    }
  }

  if (excludedFilters.length > 0) {
    excludedFilters.forEach((filter) => {
      params.append('exclusive_filters', filter);
    });
  }

  return params;
};

const TopsWidgetInner = ({ narrative, isShare, isClickable = true }) => {
  const { t } = useTranslation();
  const [actors, setActors] = useState(null);
  const [tags, setTags] = useState(null);
  const [sourceTags, setSourceTags] = useState(null);
  const [actorsPage, setActorsPage] = useState(1);
  const [tagsPage, setTagsPage] = useState(1);
  const [sourceTagsPage, setSourceTagsPage] = useState(1);
  const [widgetTab, setWidgetTab] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const {
    openModal,
    setSourceFilter,
    setSourceGroupFilter,
    setTagsFilter,
    setCurrentWidgetName,
  } = useContext(BreakdownsContext);
  const { state: filterState } = useBreakdownsFilterContext();
  const [dropdownMenu, setDropdownMenu] = useState(false);
  const [sourcesSorting, setSourcesSorting] = useState('-messages_count');

  const sortingSourcesMenu = [
    {
      name: t('Most publications'),
      value: '-messages_count',
    },
    {
      name: t('Least publications'),
      value: 'messages_count',
    },
    {
      name: t('Highest audience'),
      value: '-audience',
    },
    {
      name: t('Lowest audience'),
      value: 'audience',
    },
    {
      name: t('Highest engagement'),
      value: '-engagement',
    },
    {
      name: t('Lowest engagement'),
      value: 'engagement',
    },
  ];

  const api = isShare ? ShareAPI : API;
  const size = 15;

  const fetchTopsData = useCallback(() => {
    const urlParams = new URLSearchParams();
    urlParams.set('widget_name', `top_actors_and_tags_breakdown`);

    urlParams.set('size', size);

    const urls = [
      narrativeActorsUrl(narrative.id, 'actors', urlParams),
      narrativeActorsUrl(narrative.id, 'groups', urlParams),
      narrativeActorsUrl(narrative.id, 'tags', urlParams),
    ];

    Promise.all(urls.map((url) => api.fetch('GET', url)))
      .then(([actorsData, tagsData, sourceTagsData]) => {
        if (actorsData.objects?.length > 0) {
          setWidgetTab('actors');
        } else if (tagsData.objects?.length > 0) {
          setWidgetTab('tags');
        } else if (sourceTagsData.objects?.length > 0) {
          setWidgetTab('sourceTags');
        } else {
          setWidgetTab('actors');
        }

        setActors(actorsData);
        setTags(tagsData);
        setSourceTags(sourceTagsData);
      })
      .catch((error) => {
        showRequestError(error, t('Tops'));
        setActors({});
        setTags({});
      });
  }, [narrative.id]);

  useEffect(() => {
    fetchTopsData();
  }, [fetchTopsData]);

  const getActors = (page, appliedFilters, sorting) => {
    setIsLoading(true);
    const urlParams = new URLSearchParams();
    urlParams.set('widget_name', `top_actors_breakdown`);

    urlParams.set('sorting', sorting);

    if (appliedFilters) {
      const filterParams = buildFilterParams(appliedFilters);
      filterParams.forEach((value, key) => urlParams.append(key, value));
    }

    urlParams.set('page', page);
    urlParams.set('size', size);
    const url = narrativeActorsUrl(narrative.id, 'actors', toRollupTopActorsParams(urlParams));

    api
      .fetch('GET', url)
      .then((data) => {
        setActors(data);
        setIsLoading(false);
      })
      .catch((e) => {
        setIsLoading(false);
        showRequestError(e, t('Top actors'));
      });
  };

  const getTags = (page) => {
    setIsLoading(true);
    const urlParams = new URLSearchParams();
    urlParams.set('widget_name', `top_tags_breakdown`);
    urlParams.set('page', page);
    urlParams.set('size', size);
    const url = narrativeActorsUrl(narrative.id, 'groups', urlParams);

    api
      .fetch('GET', url)
      .then((data) => {
        setTags(data);
        setIsLoading(false);
      })
      .catch((e) => {
        setIsLoading(false);
        showRequestError(e, t('Top actor groups'));
      });
  };

  const getSourceTags = (page) => {
    setIsLoading(true);
    const urlParams = new URLSearchParams();
    urlParams.set('widget_name', `top_source_tags_breakdown`);
    urlParams.set('page', page);
    urlParams.set('size', size);
    const url = narrativeActorsUrl(narrative.id, 'tags', urlParams);

    api
      .fetch('GET', url)
      .then((data) => {
        setSourceTags(data);
      })
      .catch((error) => {
        showRequestError(error, t('Top source tags'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleApplyFilters = useCallback(() => {
    setActorsPage(1);
    getActors(1, filterState.draftFilters, sourcesSorting);
  }, [filterState?.draftFilters, sourcesSorting]);

  const tooltipStyles = {
    zIndex: 2000,
    textAlign: 'center',
    borderRadius: '6px',
    whiteSpace: 'pre-line',
    fontSize: '0.875rem',
    fontWeight: '400',
  };

  return (
    <div className="report-section" style={{ width: '100%' }} id="actors">
      <h3 className="trend-chart-controlls">
        {t('Tops')}
        {widgetTab ? (
          <button
            type="button"
            className={widgetTab === 'actors' ? 'active' : ''}
            onClick={() => {
              setWidgetTab('actors');
            }}
          >
            <span>{t('Actors')}</span>
            <span
              className={styles.tooltipControls}
              data-tooltip-id="top-actors-tooltip"
              data-tooltip-content={t('Top actors by number of posts')}
            >
              <AnchorIcon />
            </span>
          </button>
        ) : (
          ''
        )}
        {widgetTab && !isShare ? (
          <button
            type="button"
            className={widgetTab === 'tags' ? 'active' : ''}
            onClick={() => {
              setWidgetTab('tags');
            }}
          >
            <span>{t('Actor groups')}</span>
            <span
              className={styles.tooltipControls}
              data-tooltip-id="top-actor-groups-tooltip"
              data-tooltip-content={t('Top actor groups by number of posts')}
            >
              <AnchorIcon />
            </span>
          </button>
        ) : (
          ''
        )}
        {widgetTab ? (
          <button
            type="button"
            className={widgetTab === 'sourceTags' ? 'active' : ''}
            onClick={() => {
              setWidgetTab('sourceTags');
            }}
          >
            <span>{t('Actor tags')}</span>
            <span
              className={styles.tooltipControls}
              data-tooltip-id="top-source-tags-tooltip"
              data-tooltip-content={t('Top tags by number of posts')}
            >
              <AnchorIcon />
            </span>
          </button>
        ) : (
          ''
        )}
        <Tooltip
          id="top-actors-tooltip"
          data-tooltip-place="bottom"
          style={tooltipStyles}
        />
        <Tooltip
          id="top-actor-groups-tooltip"
          data-tooltip-place="bottom"
          style={tooltipStyles}
        />
        <Tooltip
          id="top-source-tags-tooltip"
          data-tooltip-place="bottom"
          style={tooltipStyles}
        />
      </h3>
      {!actors || !tags || !sourceTags ? (
        <div className="card">
          <div className={styles.widdgetLoader}>
            <LoaderSmall />
          </div>
        </div>
      ) : actors?.objects?.length > 0 ||
        tags?.objects?.length > 0 ||
        sourceTags?.objects?.length > 0 ? (
        <div>
          {widgetTab === 'actors' ? (
            <div>
              <BreakdownsContainer
                title={`${t('Actors')}: ${actors?.total || 0}`}
                onClick={() => openModal()}
                details={false}
                filtersComponent={
                  <BreakdownsFiltersContent onApply={handleApplyFilters} />
                }
                customHeaderComponent={
                  <DropdownMenu
                    isOpen={dropdownMenu}
                    onClick={() => setDropdownMenu(!dropdownMenu)}
                    menuClassName="modalSorting"
                    buttonName={`${t('Sort by')}: ${t(
                      sortingSourcesMenu.find(
                        (item) => item.value === sourcesSorting,
                      )?.name,
                    )}`}
                    icon={<ChevronIcon />}
                    frontIcon={<SortIcon />}
                  >
                    <Radiobutton
                      itemsList={sortingSourcesMenu}
                      current={sourcesSorting}
                      onChange={(value) => {
                        setSourcesSorting(value);
                        setActorsPage(1);
                        getActors(1, filterState.filters, value);
                      }}
                    />
                  </DropdownMenu>
                }
                paginationComponent={
                  actors?.total > size && (
                    <Paginator
                      size={size}
                      page={actorsPage}
                      total={actors?.total}
                      id="actors"
                      onPageChange={(page) => {
                        setActorsPage(page);
                        getActors(page, filterState.filters, sourcesSorting);
                      }}
                    />
                  )
                }
              >
                {isLoading ? (
                  <div
                    className={styles.widdgetLoader}
                    style={{ height: '719px' }}
                  >
                    <LoaderSmall />
                  </div>
                ) : actors?.objects?.length > 0 ? (
                  <div className={styles.topSourcesContainer}>
                    <div className={styles.nameHeader}>
                      <h5>{t('Actor')}</h5>
                    </div>
                    <div className={styles.messagesHeader}>
                      <h5>{t('Publications')}</h5>
                    </div>
                    <div className={styles.audienceHeader}>
                      <h5>{t('Audience')}</h5>
                    </div>
                    <div className={styles.engagementHeader}>
                      <h5>{t('Engagement')}</h5>
                    </div>
                    <div className={styles.countryHeader}>
                      <h5>{t('Country')}</h5>
                    </div>
                    <div className={styles.stateAffiliatedHeader}>
                      <h5>{t('State-affiliated')}</h5>
                    </div>
                    <div className={styles.name}>
                      <div className={styles.breakdownsListMain}>
                        {actors.objects.map((source) => {
                          return (
                            <div
                              key={`topSources_${source?.id}`}
                              className={styles.listItem}
                            >
                              <div className={styles.info}>
                                <span>
                                  {platformIconsMapping[source?.source_type]}
                                </span>
                                <button
                                  type="button"
                                  className={styles.title}
                                  data-tooltip-id="message-source"
                                  data-tooltip-content={`${decodeSourceName(
                                    source?.name,
                                  )}\n ${source?.url ?? t('N/A')}`}
                                  onClick={() => {
                                    setSourceFilter(source?.id);
                                    setCurrentWidgetName(
                                      'top_actors_by_posts_breakdown',
                                    );
                                    openModal();
                                  }}
                                  aria-label={`View details for ${source.name}`}
                                >
                                  {cutString(source.name, 40)}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <Tooltip
                        id="message-source"
                        data-tooltip-place="bottom"
                        style={tooltipStyles}
                      />
                    </div>

                    <div className={styles.messages}>
                      <div className={styles.breakdownsListMain}>
                        {actors?.objects?.map((source) => {
                          return (
                            <div
                              key={`topSources_messages_${source?.id}`}
                              className={styles.listItem}
                            >
                              <div className={styles.count}>
                                {source.messages_count}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className={styles.audience}>
                      <div className={styles.breakdownsListMain}>
                        {actors?.objects?.map((source) => {
                          let followers_cnt = source?.audience;

                          return (
                            <div
                              key={`topSources_audience_${source?.id}`}
                              className={styles.listItem}
                            >
                              <div className={styles.count}>
                                {followers_cnt === null
                                  ? ''
                                  : formatWithCustomGigaBillion(
                                      followers_cnt,
                                    )}{' '}
                                &nbsp;
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className={styles.engagement}>
                      <div className={styles.breakdownsListMain}>
                        {actors?.objects?.map((source) => {
                          return (
                            <div
                              key={`topSources_engagement_${source?.id}`}
                              className={styles.listItem}
                            >
                              <div className={styles.count}>
                                {source?.engagement != null
                                  ? formatWithCustomGigaBillion(
                                      source.engagement,
                                    )
                                  : ''}{' '}
                                &nbsp;
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className={styles.country}>
                      <div className={styles.breakdownsListMain}>
                        {actors?.objects?.map((source) => {
                          return (
                            <div
                              key={`topSources_country_${source?.id}`}
                              className={styles.listItem}
                            >
                              <div className={styles.count}>
                                {source?.origin_country
                                  ? source?.origin_country.name
                                  : ''}{' '}
                                &nbsp;{' '}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className={styles.stateAffiliated}>
                      <div className={styles.breakdownsListMain}>
                        {actors?.objects?.map((source) => {
                          return (
                            <div
                              key={`topSources_affiliated_${source?.id}`}
                              className={styles.listItem}
                            >
                              <div className={styles.count}>
                                {source?.affiliations?.length > 0
                                  ? source.affiliations
                                      .map((a) => a.country?.name || a.type)
                                      .join(', ')
                                  : ''}{' '}
                                &nbsp;
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={styles.noData}>
                    {t('There is no data to display')}
                  </div>
                )}
              </BreakdownsContainer>
            </div>
          ) : widgetTab === 'tags' && !isShare ? (
            <div>
              <BreakdownsContainer
                title={`${t('Actor groups')}: ${tags?.total || 0}`}
                onClick={() => openModal()}
                details={false}
                paginationComponent={
                  tags?.total > size && (
                    <Paginator
                      size={size}
                      page={tagsPage}
                      total={tags?.total}
                      id="tags"
                      onPageChange={(page) => {
                        setTagsPage(page);
                        getTags(page);
                      }}
                    />
                  )
                }
              >
                    {isLoading ? (
                      <div
                        className={styles.widdgetLoader}
                        style={{ height: '719px' }}
                      >
                        <LoaderSmall />
                      </div>
                    ) : tags?.objects?.length > 0 ? (
                      <div className={styles.topTagsContainer}>
                        <div className={styles.nameHeader}>
                          <h5>{t('Name')}</h5>
                        </div>
                        <div className={styles.sourcesHeader}>
                          <h5>{t('Actors')}</h5>
                        </div>
                        <div className={styles.messagesHeader}>
                          <h5>{t('Publications')}</h5>
                        </div>
                        <div className={styles.name}>
                          <div className={styles.breakdownsListMain}>
                            {tags?.objects?.map((tag) => {
                              return (
                                <div
                                  key={`tags_name_${tag?.id}`}
                                  className={styles.listItem}
                                >
                                  <div className={styles.info}>
                                    {/* <span>{platformIconsMapping[source?.source_type]}</span> */}
                                    <button
                                      type="button"
                                      className={styles.title}
                                      data-tooltip-id={`tag-tooltip`}
                                      data-tooltip-content={tag.name}
                                      onClick={() => {
                                        setSourceGroupFilter(tag.id);
                                        setCurrentWidgetName(
                                          'top_tags_breakdown',
                                        );
                                        openModal();
                                      }}
                                      aria-label={`View details for ${tag.name}`}
                                    >
                                      {cutString(tag.name, 70)}
                                    </button>
                                  </div>
                                  {/* <div className={styles.count}>{tag.messages_count}</div> */}
                                </div>
                              );
                            })}
                          </div>
                          <Tooltip
                            id={`tag-tooltip`}
                            data-tooltip-place="bottom"
                            style={tooltipStyles}
                          />
                        </div>

                        <div className={styles.sources}>
                          <div className={styles.breakdownsListMain}>
                            {tags?.objects?.map((tag) => {
                              return (
                                <div
                                  key={`tags_name_${tag?.id}`}
                                  className={styles.listItem}
                                >
                                  <div className={styles.count}>
                                    {formatNumberSignificant(
                                      tag.number_of_sources,
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <div className={styles.messages}>
                          <div className={styles.breakdownsListMain}>
                            {tags?.objects?.map((tag) => {
                              return (
                                <div
                                  key={`tags_name_${tag?.id}`}
                                  className={styles.listItem}
                                >
                                  <div className={styles.count}>
                                    {tag.messages_count}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.noData}>
                        {t('There is no data to display')}
                      </div>
                    )}
              </BreakdownsContainer>
            </div>
          ) : widgetTab === 'sourceTags' ? (
            <div>
              <BreakdownsContainer
                title={`${t('Tags')}: ${sourceTags?.total || 0}`}
                onClick={() => openModal()}
                details={false}
                paginationComponent={
                  sourceTags?.total > size && (
                    <Paginator
                      size={size}
                      page={sourceTagsPage}
                      total={sourceTags?.total}
                      id="sourceTags"
                      onPageChange={(page) => {
                        setSourceTagsPage(page);
                        getSourceTags(page);
                      }}
                    />
                  )
                }
              >
                {isLoading ? (
                  <div
                    className={styles.widdgetLoader}
                    style={{ height: '719px' }}
                  >
                    <LoaderSmall />
                  </div>
                ) : sourceTags?.objects?.length > 0 ? (
                  <div className={styles.topTagsContainer}>
                    <div className={styles.nameHeader}>
                      <h5>{t('Tag')}</h5>
                    </div>
                    <div className={styles.sourcesHeader}>
                      <h5>{t('Actors')}</h5>
                    </div>
                    <div className={styles.messagesHeader}>
                      <h5>{t('Publications')}</h5>
                    </div>
                    <div className={styles.name}>
                      <div className={styles.breakdownsListMain}>
                        {sourceTags?.objects?.map((tag) => {
                          return (
                            <div
                              key={`source_tags_name_${tag?.id}`}
                              className={styles.listItem}
                            >
                              <div className={styles.info}>
                                <button
                                  type="button"
                                  className={styles.title}
                                  data-tooltip-id="source-tag-tooltip"
                                  data-tooltip-content={tag.name}
                                  onClick={() => {
                                    if (isClickable) {
                                      setTagsFilter([tag.id]);
                                      setCurrentWidgetName(
                                        'top_source_tags_breakdown',
                                      );
                                      openModal();
                                    }
                                  }}
                                  aria-label={`View details for ${tag.name}`}
                                >
                                  {cutString(tag.name, 70)}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <Tooltip
                        id="source-tag-tooltip"
                        data-tooltip-place="bottom"
                        style={tooltipStyles}
                      />
                    </div>

                    <div className={styles.sources}>
                      <div className={styles.breakdownsListMain}>
                        {sourceTags?.objects?.map((tag) => {
                          return (
                            <div
                              key={`source_tags_sources_${tag?.id}`}
                              className={styles.listItem}
                            >
                              <div className={styles.count}>
                                {formatNumberSignificant(tag.number_of_sources)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className={styles.messages}>
                      <div className={styles.breakdownsListMain}>
                        {sourceTags?.objects?.map((tag) => {
                          return (
                            <div
                              key={`source_tags_messages_${tag?.id}`}
                              className={styles.listItem}
                            >
                              <div className={styles.count}>
                                {tag.messages_count}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={styles.noData}>
                    {t('There is no data to display')}
                  </div>
                )}
              </BreakdownsContainer>
            </div>
          ) : (
            ''
          )}
        </div>
      ) : (
        <div className="card">
          <div className={styles.noData}>
            {t('There is no data to display')}
          </div>
        </div>
      )}
    </div>
  );
};

export const TopsWidget = ({ narrative, isShare, isClickable = true }) => {
  return (
    <BreakdownsFilterProvider isShare={isShare}>
      <TopsWidgetInner
        narrative={narrative}
        isShare={isShare}
        isClickable={isClickable}
      />
    </BreakdownsFilterProvider>
  );
};
